'use client';

import React, {
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';

export type LimitIssue = {
    id?: string;
    component: string;
    field: string;
    actualLength: number;
    limit: number;
    source?: string;
    settingOwnerId?: string;
};

type PreviewLimitContextValue = {
    issues: LimitIssue[];
    report: (issue: LimitIssue) => void;
};

const PreviewLimitContext =
    createContext<PreviewLimitContextValue | null>(null);

export function usePreviewLimits() {
    return useContext(PreviewLimitContext);
}

function buildIssueKey(issue: LimitIssue) {
    return `${issue.id || ''}|${issue.component}|${issue.field}|${issue.limit}`;
}

export function PreviewLimitProvider({
                                         children,
                                     }: {
    children: React.ReactNode;
}) {
    const [issues, setIssues] = useState<LimitIssue[]>([]);

    const contextValue = useMemo<PreviewLimitContextValue>(
        () => ({
            issues,
            report: (issue: LimitIssue) => {
                if (
                    !issue ||
                    !Number.isFinite(issue.limit) ||
                    issue.actualLength <= issue.limit
                ) {
                    return;
                }

                setIssues((previousIssues) => {
                    const issueKey = buildIssueKey(issue);

                    const alreadyReported = previousIssues.some(
                        (existingIssue) =>
                            buildIssueKey(existingIssue) === issueKey
                    );

                    return alreadyReported
                        ? previousIssues
                        : [...previousIssues, issue];
                });
            },
        }),
        [issues]
    );

    return (
        <PreviewLimitContext.Provider value={contextValue}>
            {children}
        </PreviewLimitContext.Provider>
    );
}
